import { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, ChevronDown, ChevronUp, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { getParentLeaves, cancelLeaveRequest, cancelAllLeaveRequests } from "@/services/parent";
import { fontSize, fontWeight, textColor } from "@/theme";

interface LeaveItem {
  Date: string;
  Child_Id: string;
  Status: string;
  id?: number;
}

interface LeaveGroup {
  id?: number;
  Leave_Id: string;
  Username: string;
  Subscription_Code: string;
  Created_At: string;
  Leaves: LeaveItem[];
}

function statusBadge(status: string) {
  const s = status?.toLowerCase();
  if (s === "confirm") return "bg-green-100 text-green-700 border-green-200";
  if (s === "cancle" || s === "cancelled" || s === "cancel")
    return "bg-red-100 text-red-700 border-red-200";
  return "bg-yellow-100 text-yellow-700 border-yellow-200";
}

function statusLabel(status: string) {
  const s = status?.toLowerCase();
  if (s === "confirm") return "Confirmed";
  if (s === "cancle" || s === "cancelled") return "Cancelled";
  return status;
}

export default function AdminLeavesView() {
  const [leaves, setLeaves] = useState<LeaveGroup[]>([]);
  const [filtered, setFiltered] = useState<LeaveGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getParentLeaves();
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const list: LeaveGroup[] = Array.isArray(data)
          ? data
          : data?.results ?? [];
        setLeaves(list);
        setFiltered(list);
      }
    } catch (error) {
      console.error("Failed to load leaves:", error);
      // toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeaves();
  }, [loadLeaves]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFiltered(leaves);
      return;
    }
    const q = searchQuery.toLowerCase();
    setFiltered(
      leaves.filter(
        (l) =>
          l.Username?.toLowerCase().includes(q) ||
          l.Leave_Id?.toLowerCase().includes(q) ||
          l.Subscription_Code?.toLowerCase().includes(q)
      )
    );
  }, [searchQuery, leaves]);

  const toggleExpand = (leaveId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(leaveId) ? next.delete(leaveId) : next.add(leaveId);
      return next;
    });
  };

  const handleCancelAll = async (group: LeaveGroup) => {
    if (!window.confirm(`Cancel all leaves in request ${group.Leave_Id}?`)) return;
    setCancellingId(group.Leave_Id);
    try {
      const response = await cancelAllLeaveRequests(group.Leave_Id, { Status: "Cancle" });
      if (response.status === 200 || response.status === 204) {
        toast.success("All leaves cancelled successfully");
        loadLeaves();
      } else {
        toast.error("Failed to cancel leave request");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Something went wrong");
    } finally {
      setCancellingId(null);
    }
  };

  const handleCancelSingle = async (leafId: number, group: LeaveGroup) => {
    if (!window.confirm("Cancel this specific leave entry?")) return;
    setCancellingId(`${group.Leave_Id}-${leafId}`);
    try {
      const response = await cancelLeaveRequest(leafId, { Status: "Cancle" });
      if (response.status === 200 || response.status === 204) {
        toast.success("Leave entry cancelled");
        loadLeaves();
      } else {
        toast.error("Failed to cancel leave");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error("Something went wrong");
    } finally {
      setCancellingId(null);
    }
  };

  const totalLeaves = leaves.length;
  const activeLeaves = leaves.filter((l) =>
    l.Leaves?.some((li) => li.Status?.toLowerCase() === "confirm")
  ).length;
  const cancelledLeaves = leaves.filter((l) =>
    l.Leaves?.every((li) => ["cancle", "cancelled"].includes(li.Status?.toLowerCase()))
  ).length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <p className={`${fontSize.xs} ${textColor.muted} font-medium`}>Total Requests</p>
            <p className={`${fontSize['2xl']} ${fontWeight.bold} ${textColor.heading}`}>{totalLeaves}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className={`${fontSize.xs} ${textColor.muted} font-medium`}>Active Requests</p>
            <p className={`${fontSize['2xl']} ${fontWeight.bold} text-green-700`}>{activeLeaves}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className={`${fontSize.xs} ${textColor.muted} font-medium`}>Cancelled</p>
            <p className={`${fontSize['2xl']} ${fontWeight.bold} text-red-700`}>{cancelledLeaves}</p>
          </div>
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by username, Leave ID or subscription code…"
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </div>
          <button
            onClick={loadLeaves}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Leaves List */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-500 mx-auto mb-3" />
          <p className={`${textColor.muted} text-sm`}>Loading leave requests…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className={`${fontWeight.semibold} ${textColor.heading} mb-1`}>No leave requests found</p>
          <p className={`text-sm ${textColor.muted}`}>
            {searchQuery ? "Try a different search term" : "No leave requests have been submitted yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((group) => {
            const isExpanded = expandedIds.has(group.Leave_Id);
            const isCancelling = cancellingId === group.Leave_Id;
            const allCancelled = group.Leaves?.every((l) =>
              ["cancle", "cancelled"].includes(l.Status?.toLowerCase())
            );
            const createdDate = new Date(group.Created_At).toLocaleDateString("en-IN", {
              day: "2-digit", month: "short", year: "numeric",
            });

            return (
              <div key={group.Leave_Id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                {/* Group Header */}
                <div className="flex items-start justify-between p-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`${fontWeight.bold} ${textColor.heading} text-sm`}>{group.Leave_Id}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-sm text-brand-500 font-semibold">{group.Username}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-gray-500">
                        Sub: <span className="font-medium text-gray-700">{group.Subscription_Code}</span>
                      </span>
                      <span className="text-xs text-gray-400">{createdDate}</span>
                      <span className="text-xs text-gray-500">
                        {group.Leaves?.length ?? 0} leave entries
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {!allCancelled && (
                      <button
                        onClick={() => handleCancelAll(group)}
                        disabled={isCancelling}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        {isCancelling ? "Cancelling…" : "Cancel"}
                      </button>
                    )}
                    <button
                      onClick={() => toggleExpand(group.Leave_Id)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Expanded: individual leaves */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50">
                    <div className="p-4 space-y-2">
                      {group.Leaves?.map((leaf, idx) => {
                        const leafKey = `${group.Leave_Id}-${idx}`;
                        const isCancellingLeaf = cancellingId === leafKey;
                        const isCancelled = ["cancle", "cancelled"].includes(leaf.Status?.toLowerCase());
                        return (
                          <div
                            key={idx}
                            className="flex items-center justify-between bg-white rounded-lg px-4 py-3 border border-gray-100"
                          >
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{leaf.Date}</p>
                                <p className="text-xs text-gray-500">{leaf.Child_Id}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusBadge(leaf.Status)}`}>
                                {statusLabel(leaf.Status)}
                              </span>
                              {!isCancelled && leaf.id && (
                                <button
                                  onClick={() => handleCancelSingle(leaf.id!, group)}
                                  disabled={isCancellingLeaf}
                                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors disabled:opacity-50"
                                >
                                  {isCancellingLeaf ? "…" : "Cancel"}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
